import { useEffect, useState } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

const WA_NUMBER = '918308595553';
const WA_URL_BASE = `https://wa.me/${WA_NUMBER}`;

/**
 * Premium, wellness-focused floating WhatsApp button.
 * - White + soft indigo (Calm-app style)
 * - Subtle non-aggressive pulse
 * - Expandable tooltip on desktop hover
 * - Dismissible hint bubble that returns on next page load
 */
function FloatingWhatsApp() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  // Build wa.me URL with the localized opening message
  const waMessage = `Hi Clinity, I want to take the wellness assessment and get my personalized roadmap. Please guide me.`;
  const waHi = `नमस्ते Clinity, मैं वेलनेस टेस्ट देना चाहता/चाहती हूँ। कृपया मार्गदर्शन करें।`;
  const waMr = `नमस्कार Clinity, मला वेलनेस टेस्ट द्यायचा आहे. कृपया मार्गदर्शन करा.`;
  const { lang } = useTranslation();
  const localizedMsg = lang === 'hi' ? waHi : lang === 'mr' ? waMr : waMessage;
  const WA_URL_LOCALIZED = `${WA_URL_BASE}?text=${encodeURIComponent(localizedMsg)}`;

  useEffect(() => {
    // Show the hint bubble 4 seconds after page settles; stay for 7s; respect dismissal.
    try {
      const wasDismissed = sessionStorage.getItem('clinity_wa_hint_dismissed') === '1';
      if (wasDismissed) { setHintDismissed(true); return; }
    } catch { /* noop */ }

    const openTimer = setTimeout(() => setHintOpen(true), 4000);
    const closeTimer = setTimeout(() => setHintOpen(false), 11000);
    return () => { clearTimeout(openTimer); clearTimeout(closeTimer); };
  }, []);

  const dismissHint = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setHintOpen(false);
    setHintDismissed(true);
    try { sessionStorage.setItem('clinity_wa_hint_dismissed', '1'); } catch { /* noop */ }
  };

  return (
    <>
      {/* Tooltip/Hint bubble — desktop hover OR timed pop */}
      {(expanded || (hintOpen && !hintDismissed)) && (
        <div
          className="fixed bottom-[92px] right-4 md:right-6 z-[60] pointer-events-auto animate-[waFadeUp_320ms_ease-out]"
          data-testid="wa-hint-bubble"
        >
          <div className="relative bg-white border border-indigo-100 rounded-2xl shadow-[0_12px_40px_-8px_rgba(79,70,229,0.25)] px-4 py-3 pr-9 max-w-[260px]">
            <button
              type="button"
              onClick={dismissHint}
              aria-label="Dismiss WhatsApp hint"
              className="absolute top-1.5 right-1.5 p-1 rounded-full text-indigo-300 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
              data-testid="wa-hint-dismiss"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2.5} />
            </button>
            <p className="text-[13px] font-semibold text-[#0F172A] leading-tight">
              {t('wa.float.title')}
            </p>
            <p className="text-[11px] text-[#64748B] mt-0.5 leading-snug">
              {t('wa.float.subtitle')}
            </p>
            {/* Speech-bubble pointer */}
            <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-b border-r border-indigo-100 rotate-45"></span>
          </div>
        </div>
      )}

      <a
        href={WA_URL_LOCALIZED}
        target="_top"
        rel="noopener noreferrer"
        aria-label="Chat with Clinity on WhatsApp"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocus={() => setExpanded(true)}
        onBlur={() => setExpanded(false)}
        onClick={dismissHint}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[55] group"
        data-testid="floating-whatsapp-btn"
      >
        {/* Slow ambient pulse — indigo ring, not aggressive */}
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-indigo-400/25 animate-[waPulse_2.6s_ease-out_infinite]"
        />
        <span
          aria-hidden="true"
          className="absolute inset-0 rounded-full bg-indigo-400/15 animate-[waPulse_2.6s_ease-out_infinite] [animation-delay:1.3s]"
        />

        <div
          className="relative flex items-center gap-2 h-14 pl-[14px] pr-[16px] rounded-full bg-white border border-indigo-100 shadow-[0_10px_32px_-8px_rgba(79,70,229,0.35)] transition-all duration-300 ease-out
                     hover:shadow-[0_18px_48px_-8px_rgba(79,70,229,0.45)] hover:-translate-y-0.5 hover:border-indigo-200
                     active:translate-y-0 active:scale-[0.97]"
        >
          <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#4F46E5] to-[#6366F1] text-white shadow-inner">
            {/* Official WhatsApp glyph for instant recognition, with indigo bg for brand harmony */}
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M19.11 4.91A10.05 10.05 0 0 0 12.05 2C6.58 2 2.13 6.45 2.13 11.92c0 2.1.55 4.15 1.6 5.96L2 22l4.25-1.11a9.92 9.92 0 0 0 5.8 1.85h.01c5.47 0 9.92-4.45 9.92-9.92 0-2.65-1.03-5.14-2.87-7.02zM12.05 20.9h-.01a8.2 8.2 0 0 1-4.17-1.14l-.3-.18-2.52.66.67-2.46-.2-.31a8.23 8.23 0 0 1-1.26-4.38c0-4.54 3.69-8.23 8.23-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.69 8.23-8.24 8.23zm4.52-6.16c-.25-.12-1.47-.73-1.7-.81-.23-.08-.4-.13-.56.13-.17.25-.65.81-.8.98-.15.17-.3.19-.55.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.5.11-.11.25-.3.38-.44.12-.15.17-.25.25-.42.08-.17.04-.32-.02-.44-.06-.12-.56-1.35-.76-1.85-.2-.48-.41-.42-.56-.43l-.48-.01a.93.93 0 0 0-.68.32c-.23.25-.89.87-.89 2.12 0 1.25.91 2.47 1.04 2.64.13.17 1.8 2.74 4.37 3.85.61.26 1.09.42 1.46.54.61.2 1.17.17 1.61.1.49-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.15-1.18-.06-.11-.23-.17-.48-.3z"/>
            </svg>
          </span>
          <span
            className={`overflow-hidden whitespace-nowrap text-[13px] font-semibold text-[#0F172A] transition-all duration-300 ease-out
                        ${expanded ? 'max-w-[240px] opacity-100 ml-0.5' : 'max-w-0 opacity-0'} md:group-hover:max-w-[240px] md:group-hover:opacity-100`}
          >
            Talk to Us on WhatsApp
          </span>
        </div>
      </a>

      {/* Keyframes — scoped via Tailwind arbitrary classes above */}
      <style>{`
        @keyframes waPulse {
          0%   { transform: scale(0.85); opacity: 0.6; }
          70%  { transform: scale(1.35); opacity: 0; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes waFadeUp {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

export default FloatingWhatsApp;
export { WA_NUMBER };
